import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ShieldCheck, ArrowRight, Zap, MessageSquare, PhoneCall, XCircle, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Exact web opt-in disclosure (RoofLead platform development & testing program). Re-file the opt-in sample with Twilio if changed.
export const SMS_CONSENT_TEXT =
  "By checking this box, I agree to receive text messages from Brandon Rinker Jr. following up on my inquiry, as part of the " +
  "RoofLead platform development & testing program. RoofLead is software and does not provide roofing services. " +
  "Message frequency varies. Message and data rates may apply. " +
  "Reply STOP to opt out or HELP for help. Consent is not a condition of submitting an inquiry.";

export default function GetQuote() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    caller_name: "",
    caller_phone: "",
    issue: "",
  });
  const [smsConsent, setSmsConsent] = useState(false); // UNCHECKED by default, OPTIONAL
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const canSubmit =
    form.caller_name.trim().length > 1 &&
    form.caller_phone.trim().length >= 7 &&
    !loading;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/lead-intake`, {
        caller_name: form.caller_name,
        caller_phone: form.caller_phone,
        issue: form.issue || "General Inquiry",
        sms_consent: smsConsent,
        consent_text: smsConsent
          ? `${SMS_CONSENT_TEXT} See our Privacy Policy (${window.location.origin}/privacy) and Terms of Service (${window.location.origin}/terms).`
          : null,
        form_url: typeof window !== "undefined" ? window.location.href : null,
      });
      setDone({ ...data, name: form.caller_name });
      toast.success("Your inquiry was received.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const optedIn = Boolean(done.sms_opted_in);
    return (
      <div className="min-h-screen bg-[#0B0F17] text-slate-100">
        <nav className="border-b border-slate-800 bg-[#0B0F17]/85 backdrop-blur-md">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
            <Logo />
            <Link to="/" className="text-sm text-slate-400 hover:text-white" data-testid="confirm-nav-home">← Home</Link>
          </div>
        </nav>
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <Card className="bg-slate-900/90 border-emerald-500/40 p-8 sm:p-10 rounded-2xl text-center" data-testid="quote-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight mb-3">Thanks, {done.name.split(" ")[0]} — we got your inquiry.</h1>
            <p className="text-slate-300 mb-6">
              RoofLead has recorded your inquiry as part of the RoofLead platform development &amp; testing program.
              RoofLead is software and does not provide roofing services, and inquiries are not currently routed to any
              roofing company. A RoofLead team member may follow up about your inquiry.
            </p>

            {optedIn ? (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-4 text-left" data-testid="confirm-opted-in">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-emerald-200 mb-1">You opted in to texts from Brandon Rinker Jr. (RoofLead platform development &amp; testing program).</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      We may send you text messages following up on your inquiry.
                      Message frequency varies. Message & data rates may apply.
                      Reply <span className="font-mono text-emerald-300">STOP</span> at any time to opt out, or <span className="font-mono text-emerald-300">HELP</span> for help.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-4 text-left" data-testid="confirm-not-opted-in">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-200 mb-1">You did not opt in to SMS.</p>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      No text messages will be sent to you. Brandon Rinker Jr. may follow up by phone instead.
                      You can opt in later any time by resubmitting this form.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={() => navigate("/")} data-testid="quote-back-home" className="mt-8 bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold">
              Back to home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <nav className="border-b border-slate-800 bg-[#0B0F17]/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-slate-400 hover:text-white" data-testid="quote-nav-home">← Back to home</Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16 grid lg:grid-cols-5 gap-8 lg:gap-12">
        <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Badge className="bg-orange-500/10 text-orange-300 border border-orange-500/30 hover:bg-orange-500/10 mb-6 font-mono text-xs tracking-widest" data-testid="quote-testing-badge">
            <Zap className="w-3 h-3 mr-1" /> Development &amp; Testing Program
          </Badge>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] mb-5">
            Contact RoofLead.
          </h1>
          <p className="text-slate-300 leading-relaxed mb-8" data-testid="quote-testing-notice">
            RoofLead is a software platform in development and testing. RoofLead is not a roofing contractor and does not
            provide roofing services, inspections, estimates, or roofing work. Inquiries submitted here are part of the
            RoofLead platform development &amp; testing program and are not currently routed to any roofing company.
          </p>
          <ul className="space-y-3 text-slate-300">
            <li className="flex gap-3 items-start"><PhoneCall className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /> Reply by phone or text — your choice</li>
            <li className="flex gap-3 items-start"><MessageSquare className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /> Follow-up texts only if you opt in — no promotional messages</li>
            <li className="flex gap-3 items-start"><ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /> Your phone number is never sold or shared for marketing</li>
          </ul>
        </motion.div>

        <motion.div className="lg:col-span-3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-slate-900 border-slate-800 p-5 sm:p-8 rounded-2xl">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6">Contact RoofLead</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="quote-form" noValidate>
              <div>
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" required data-testid="quote-name" autoComplete="name" value={form.caller_name} onChange={set("caller_name")} className="mt-1 bg-slate-800 border-slate-700" />
              </div>
              <div>
                <Label htmlFor="phone">Mobile phone *</Label>
                <Input id="phone" required type="tel" data-testid="quote-phone" autoComplete="tel" value={form.caller_phone} onChange={set("caller_phone")} placeholder="(555) 123-4567" className="mt-1 bg-slate-800 border-slate-700" />
              </div>
              <div>
                <Label htmlFor="issue">Inquiry / message</Label>
                <Textarea id="issue" data-testid="quote-issue" value={form.issue} onChange={set("issue")} placeholder="Your message to RoofLead…" rows={5} className="mt-1 bg-slate-800 border-slate-700 resize-none" />
              </div>

              {/* OPTIONAL service-SMS consent — unchecked by default. Form MUST remain submittable without it. */}
              <div className="mt-6 p-4 rounded-lg bg-slate-800/60 border border-slate-800">
                <label className="flex gap-3 items-start cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="quote-sms-consent"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 shrink-0 rounded border-slate-600 bg-slate-900 accent-orange-500"
                  />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    {SMS_CONSENT_TEXT}{" "}See our{" "}
                    <Link to="/privacy" target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300 underline" data-testid="quote-privacy-link">Privacy Policy</Link>
                    {" "}and{" "}
                    <Link to="/terms" target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300 underline" data-testid="quote-terms-link">Terms of Service</Link>.
                  </span>
                </label>
              </div>

              <Button type="submit" disabled={!canSubmit} data-testid="quote-submit" className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-bold h-12 mt-2">
                {loading ? "Sending…" : <>Submit inquiry <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Brandon Rinker Jr. will not sell or share your mobile phone number or SMS opt-in information with third parties or affiliates for marketing or promotional purposes.
              </p>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
